import { useEffect, useState } from 'react';
import { Loader2, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Preview {
  driverId: string;
  driverName: string;
  driverCode: string;
  month: string;
  payModel: 'fixed_monthly' | 'base_plus_per_trip';
  baseSalary: number;
  tripBonus: number;
  tripsCount: number;
  tripsBreakdown: Array<{ tripCode: string; route: string; amount: number }>;
  deductions: { outstandingAdvances: number; reconciledExpenses: number; other: number };
  gross: number;
  netPayable: number;
}

interface SalaryPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driverId: string;
  defaultMonth?: string;
  onCommitted?: () => void;
}

const fmt = (n: number) => 'SAR ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function SalaryPreviewModal({ open, onOpenChange, driverId, defaultMonth, onCommitted }: SalaryPreviewModalProps) {
  const [month, setMonth] = useState(defaultMonth ?? new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null);
    apiClient.post<Preview>('/api/v1/driver-finance/salary-runs/preview', { driverId, month })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) setError(extractErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, driverId, month]);

  async function commit() {
    if (!preview) return;
    if (!window.confirm(`Commit salary run for ${preview.driverName} for ${preview.month}? This marks outstanding advances as recovered.`)) return;
    setCommitting(true);
    try {
      const r = await apiClient.post<{ salaryRunId: string; pdfUrl: string }>('/api/v1/driver-finance/salary-runs/commit', { driverId, month });
      // Download the PDF immediately
      const jwt = getStoredToken();
      const pdfRes = await fetch(API_BASE + r.data.pdfUrl, { headers: jwt ? { Authorization: 'Bearer ' + jwt } : {} });
      if (pdfRes.ok) {
        const blob = await pdfRes.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `salary-${preview.driverCode}-${month}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      onCommitted?.();
      onOpenChange(false);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setCommitting(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Salary preview</SheetTitle>
          <SheetDescription>Live computation from pay_model_v2 + completed trips + outstanding advances.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="space-y-1">
            <Label>Period (YYYY-MM)</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <PageError error={error} />
          ) : preview ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">{preview.driverName} · {preview.driverCode} · {preview.payModel.replace(/_/g, ' ')}</div>
                <div className="text-3xl font-light mt-1">{fmt(preview.netPayable)}</div>
                <div className="text-xs text-muted-foreground mt-1">Net payable for {preview.month}</div>
              </div>

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">Earnings</div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-3 py-2">Base salary</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(preview.baseSalary)}</td>
                    </tr>
                    {preview.payModel === 'base_plus_per_trip' && preview.tripsBreakdown.map((t) => (
                      <tr key={t.tripCode} className="border-b border-border">
                        <td className="px-3 py-2 pl-6 text-muted-foreground">{t.tripCode} · {t.route}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(t.amount)}</td>
                      </tr>
                    ))}
                    {preview.tripsBreakdown.length > 0 && (
                      <tr className="border-b border-border">
                        <td className="px-3 py-2">Trips subtotal ({preview.tripsCount})</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(preview.tripBonus)}</td>
                      </tr>
                    )}
                    <tr className="bg-muted/20">
                      <td className="px-3 py-2 font-medium">Gross</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">{fmt(preview.gross)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">Deductions</div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-3 py-2">Outstanding advances</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">−{fmt(preview.deductions.outstandingAdvances)}</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-3 py-2">Reconciled expenses (this month)</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">−{fmt(preview.deductions.reconciledExpenses)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="rounded-lg border-2 border-foreground p-3 bg-muted/10">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Net payable</div>
                <div className="text-2xl font-medium mt-1">{fmt(preview.netPayable)}</div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={committing}>Cancel</Button>
          <Button onClick={commit} disabled={committing || !preview || preview.netPayable < 0} className="gap-1">
            {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {committing ? 'Committing…' : 'Confirm & download PDF'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
